import { SchemaDefinition, DuplicateRecordCriteria } from "./types";
import { Location } from "@/resources/types";
import { StatusValues } from "@/resources/types";
import { optionsSex, optionsGender, optionsPronouns, optionsRelationToPatient } from "@/TrierStandards";

const parseLocationCapacity = (raw: string) => {
  const t = raw.trim();
  if (t === "") return null;
  if (!/^\d+$/.test(t)) {
    throw new Error("Capacity must be a non-negative whole number or empty for unlimited");
  }
  return Number(t);
};

// Location Schema
export const locationSchema: SchemaDefinition<Location> = {
  fields: [
    {
      name: "name",
      type: "string",
      label: "Name",
      required: true,
      flex: 1,
      editable: false,
    },
    {
      name: "shortName",
      type: "string",
      label: "Short Name",
      required: false,
      width: 250,
      editable: false,
    },
    {
      name: "area",
      type: "string",
      label: "Area",
      required: false,
      width: 200,
      editable: false,
      valueFormatter: (value) => value || "Not set",
    },
    {
      name: "capacity",
      type: "number",
      label: "Capacity",
      required: false,
      width: 120,
      editable: false,
      defaultValue: null,
      numberMin: 0,
      editHelperText: "Leave empty for unlimited capacity, or enter a whole number",
      valueFormatter: (value) =>
        value === null || value === undefined || value === "" ? "Unlimited" : String(value),
      valueParser: parseLocationCapacity,
    },
    {
      name: "status",
      type: "enum",
      label: "Status",
      required: true,
      width: 250,
      editable: false,
      enumValues: StatusValues,
    },
  ],
  csvColumnOrder: ["name", "shortName", "area", "capacity", "status"],
  defaultValues: {
    name: "",
    shortName: "",
    area: "",
    capacity: null,
    status: "active",
  },
  entityName: "Location",
  entityNamePlural: "Locations",
};

export const locationDuplicateCriteria: DuplicateRecordCriteria = {
  fields: ["shortName"],
  logic: "AND",
};

// ClinicianId Schema
export type ClinicianIdRecord = {
  id: string;
  clinicianId: string;
  identityId: string;
  firstName?: string;
  lastName?: string;
  operatorCode?: string;
  reference?: string;
};

export const clinicianIdSchema: SchemaDefinition<ClinicianIdRecord> = {
  fields: [
    {
      name: "clinicianId",
      type: "string",
      label: "Clinician ID",
      required: true,
      flex: 1,
      editable: false,
    },
    {
      name: "identityId",
      type: "string",
      label: "Identity ID",
      required: true,
      flex: 1,
      editable: false,
    },
    {
      name: "firstName",
      type: "string",
      label: "First name",
      required: false,
      flex: 1,
      editable: false,
    },
    {
      name: "lastName",
      type: "string",
      label: "Last name",
      required: false,
      flex: 1,
      editable: false,
    },
    {
      name: "operatorCode",
      type: "string",
      label: "Operator code",
      required: false,
      flex: 1,
      editable: false,
      inputType: "password",
      valueFormatter: (value: any) => (value ? "••••••" : ""),
    },
    {
      name: "reference",
      type: "string",
      label: "Reference",
      required: false,
      flex: 1,
      editable: false,
    },
  ],
  csvColumnOrder: ["clinicianId", "identityId", "firstName", "lastName", "operatorCode", "reference"],
  defaultValues: {
    clinicianId: "",
    identityId: "",
    firstName: "",
    lastName: "",
    operatorCode: "",
    reference: "",
  },
  entityName: "Clinician ID",
  entityNamePlural: "Clinician IDs",
};

export const clinicianIdDuplicateCriteria: DuplicateRecordCriteria = {
  fields: ["clinicianId"],
  logic: "AND",
};

// Option Schema (for backward compatibility)
export type OptionRecord = {
  id: string;
  code: string;
  description: string;
  public?: string;
  active: boolean;
};

export const optionSchema: SchemaDefinition<OptionRecord> = {
  fields: [
    {
      name: "code",
      type: "string",
      label: "Code",
      required: true,
      width: 200,
      editable: false,
    },
    {
      name: "description",
      type: "string",
      label: "Description",
      required: true,
      flex: 1,
      editable: false,
    },
    {
      name: "active",
      type: "boolean",
      label: "Active",
      required: true,
      width: 100,
      editable: false,
      valueFormatter: (value) => (value ? "Yes" : "No"),
    },
  ],
  csvColumnOrder: ["code", "description", "active"],
  defaultValues: {
    code: "",
    description: "",
    active: true,
  },
  entityName: "Option",
  entityNamePlural: "Options",
};

export const optionDuplicateCriteria: DuplicateRecordCriteria = {
  fields: ["code"],
  logic: "AND",
};

// Helper function to create option schema with public field
const createOptionSchemaWithPublic = (
  publicOptions: Array<{ value: string; label: string }>
): SchemaDefinition<OptionRecord> => {
  return {
    fields: [
      {
        name: "code",
        type: "string",
        label: "Code",
        required: true,
        width: 200,
        editable: false,
      },
      {
        name: "description",
        type: "string",
        label: "Description",
        required: true,
        flex: 1,
        editable: false,
      },
      {
        name: "public",
        type: "select",
        label: "Public",
        required: false,
        width: 200,
        editable: true,
        selectOptions: publicOptions,
        valueFormatter: (value) => {
          if (!value) return "";
          const option = publicOptions.find((opt) => opt.value === value);
          return option ? option.label : value;
        },
      },
      {
        name: "active",
        type: "boolean",
        label: "Active",
        required: true,
        width: 100,
        editable: false,
        valueFormatter: (value) => (value ? "Yes" : "No"),
      },
    ],
    csvColumnOrder: ["code", "description", "public", "active"],
    defaultValues: {
      code: "",
      description: "",
      public: "",
      active: true,
    },
    entityName: "Option",
    entityNamePlural: "Options",
  };
};

// Sex Options Schema
export const sexOptionsSchema = createOptionSchemaWithPublic(optionsSex);

// Gender Options Schema
export const genderOptionsSchema = createOptionSchemaWithPublic(optionsGender);

// Pronouns Options Schema
export const pronounsOptionsSchema = createOptionSchemaWithPublic(optionsPronouns);

// Delegate Relationship Options Schema
export const delegateOptionsSchema = createOptionSchemaWithPublic(optionsRelationToPatient);

